import { useState } from 'react';
import type { Transaction, TransactionSummary, MonthlyStats, SelectedRegion } from '../types';
import { fetchTransactions, calculateSummary, calculateMonthlyStats } from '../lib/api';
import { generateExcel } from '../lib/exportExcel';

export default function ReportPage() {
  const [selectedRegion, _setSelectedRegion] = useState<SelectedRegion | null>(null);
  const [transactions, setTransactions] = useState<Transaction[]>([]);
  const [summaries, setSummaries] = useState<TransactionSummary[]>([]);
  const [monthlyStats, setMonthlyStats] = useState<MonthlyStats[]>([]);
  const [loading, setLoading] = useState(false);

  // 분석 설정
  const [analysisSettings, setAnalysisSettings] = useState({
    includePriceTrend: true,
    includeComparison: true,
    includeOutliers: true,
    customAnalysis: '',
  });

  const handleGenerateReport = async () => {
    if (!selectedRegion) return;

    setLoading(true);
    try {
      const data = await fetchTransactions(selectedRegion.dong);
      setTransactions(data);
      setSummaries(calculateSummary(data));
      setMonthlyStats(calculateMonthlyStats(data));
    } finally {
      setLoading(false);
    }
  };

  const handleExport = () => {
    if (transactions.length > 0 && selectedRegion) {
      generateExcel(
        transactions,
        summaries,
        `${selectedRegion.sidoName}_${selectedRegion.sigunguName}_${selectedRegion.dongName}_분석레포트`
      );
    }
  };

  return (
    <div className="space-y-6">
      <div className="card">
        <h1 className="text-2xl font-bold mb-4">📊 아파트 가격 동향 분석 레포트</h1>
        <p className="text-gray-600 mb-6">
          선택한 지역의 실거래가 데이터를 분석하여 맞춤형 레포트를 생성합니다.
        </p>

        {/* 분석 설정 */}
        <div className="space-y-4 mb-6">
          <h2 className="font-semibold">분석 항목 설정</h2>
          
          <label className="flex items-center gap-2">
            <input
              type="checkbox"
              checked={analysisSettings.includePriceTrend}
              onChange={(e) => setAnalysisSettings({
                ...analysisSettings,
                includePriceTrend: e.target.checked
              })}
              className="w-4 h-4"
            />
            <span>가격 추세 분석</span>
          </label>

          <label className="flex items-center gap-2">
            <input
              type="checkbox"
              checked={analysisSettings.includeComparison}
              onChange={(e) => setAnalysisSettings({
                ...analysisSettings,
                includeComparison: e.target.checked
              })}
              className="w-4 h-4"
            />
            <span>아파트 간 비교 분석</span>
          </label>

          <label className="flex items-center gap-2">
            <input
              type="checkbox"
              checked={analysisSettings.includeOutliers}
              onChange={(e) => setAnalysisSettings({
                ...analysisSettings,
                includeOutliers: e.target.checked
              })}
              className="w-4 h-4"
            />
            <span>이상치 탐지</span>
          </label>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              추가 분석 기법 (준비 중)
            </label>
            <textarea
              className="input h-20"
              placeholder="분석에 적용할 추가 기법을 입력하세요..."
              value={analysisSettings.customAnalysis}
              onChange={(e) => setAnalysisSettings({
                ...analysisSettings,
                customAnalysis: e.target.value
              })}
            />
          </div>
        </div>

        {/* 레포트 생성 버튼 */}
        <button
          className="btn btn-primary"
          onClick={handleGenerateReport}
          disabled={!selectedRegion || loading}
        >
          {loading ? '분석 중...' : '레포트 생성'}
        </button>
      </div>

      {/* 분석 결과 */}
      {summaries.length > 0 && (
        <>
          {/* 요약 통계 */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div className="card text-center">
              <div className="text-3xl font-bold text-primary-600">
                {summaries.length}
              </div>
              <div className="text-sm text-gray-600">아파트 단지</div>
            </div>
            <div className="card text-center">
              <div className="text-3xl font-bold text-green-600">
                {transactions.length}
              </div>
              <div className="text-sm text-gray-600">거래 건수</div>
            </div>
            <div className="card text-center">
              <div className="text-3xl font-bold text-blue-600">
                {Math.round(summaries.reduce((acc, s) => acc + s.avgPrice, 0) / summaries.length) / 10000}억
              </div>
              <div className="text-sm text-gray-600">평균 매매가</div>
            </div>
            <div className="card text-center">
              <div className="text-3xl font-bold text-purple-600">
                {Math.round(summaries.reduce((acc, s) => acc + s.avgPricePerPyeong, 0) / summaries.length).toLocaleString()}만
              </div>
              <div className="text-sm text-gray-600">평균 평당가</div>
            </div>
          </div>

          {/* 가격 추세 */}
          {analysisSettings.includePriceTrend && monthlyStats.length > 0 && (
            <div className="card">
              <h2 className="text-lg font-semibold mb-4">📈 가격 추세</h2>
              <p className="text-gray-600">
                월별 거래량과 평균 가격의 변화를 분석합니다.
              </p>
              <div className="mt-4 overflow-x-auto">
                <table className="table">
                  <thead>
                    <tr>
                      <th>월</th>
                      <th>거래건수</th>
                      <th>평균가</th>
                      <th>최저가</th>
                      <th>최고가</th>
                    </tr>
                  </thead>
                  <tbody>
                    {monthlyStats.slice(0, 12).map((stat) => (
                      <tr key={stat.month}>
                        <td>{stat.month}</td>
                        <td>{stat.count}건</td>
                        <td>{(stat.avgPrice / 10000).toFixed(1)}억</td>
                        <td>{(stat.minPrice / 10000).toFixed(1)}억</td>
                        <td>{(stat.maxPrice / 10000).toFixed(1)}억</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* 아파트 간 비교 */}
          {analysisSettings.includeComparison && (
            <div className="card">
              <h2 className="text-lg font-semibold mb-4">🔄 아파트 간 비교</h2>
              <div className="overflow-x-auto">
                <table className="table">
                  <thead>
                    <tr>
                      <th>순위</th>
                      <th>아파트명</th>
                      <th>준공년도</th>
                      <th>평당가</th>
                      <th>거래건수</th>
                    </tr>
                  </thead>
                  <tbody>
                    {summaries.slice(0, 15).map((summary, index) => (
                      <tr key={summary.aptName}>
                        <td>{index + 1}</td>
                        <td className="font-medium">{summary.aptName}</td>
                        <td>{summary.buildYear}년</td>
                        <td>{summary.avgPricePerPyeong.toLocaleString()}만</td>
                        <td>{summary.transactionCount}건</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* 내보내기 */}
          <div className="flex justify-end">
            <button
              className="btn btn-primary"
              onClick={handleExport}
            >
              📊 레포트 다운로드 (Excel)
            </button>
          </div>
        </>
      )}

      {/* 안내 */}
      <div className="card bg-yellow-50">
        <h3 className="font-semibold text-yellow-800 mb-2">💡 분석 기법 안내</h3>
        <ul className="text-sm text-yellow-700 space-y-1">
          <li>• <strong>가격 추세 분석</strong>: 월별 가격 변화를 분석합니다.</li>
          <li>• <strong>아파트 간 비교</strong>: 평당가 기준 순위를 매깁니다.</li>
          <li>• <strong>이상치 탐지</strong>: 평균에서 벗어난 거래를 찾습니다.</li>
          <li>• <strong>추가 분석 기법</strong>: 원하는 분석 방법을 입력하세요.</li>
        </ul>
      </div>
    </div>
  );
}